import React from 'react';
import { ChevronDown } from 'lucide-react';
import { rupees } from '@/components/myPay/i18n';

const HOLD = { hr: 'hrDecide', fix: 'missed', waiting: 'waitingAppr', open: 'inProgress' };
const NOTE = { hr: 'hrNote', fix: 'fixNote', waiting: 'waitNote', open: 'openNote' };

const holdLabel = (p, t) => (p.hold ? t(HOLD[p.hold]) : p.norate ? t('rateNotSet') : null);

/** Pay column of one day: final amount, or why it is held back (HR to decide / waiting approval). */
export const PayCell = ({ d, t, open }) => {
  if (!d.pay) return null;
  const label = holdLabel(d.pay, t);
  return (
    <div className="flex items-center justify-end gap-1 text-right" data-testid={`mypay-daypay-${d.date}`}>
      <div className="leading-tight">
        {(d.pay.amount > 0 || !label) && <div className="font-semibold tabular-nums text-[#1A1F2E]" data-testid={`mypay-daypay-amt-${d.date}`}>{rupees(d.pay.amount)}</div>}
        {label && <div className="text-[11px] font-semibold text-amber-700" data-testid={`mypay-daypay-hold-${d.date}`}>{label}</div>}
      </div>
      <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform print:hidden ${open ? 'rotate-180' : ''}`} />
    </div>
  );
};

const Line = ({ left, right, testId }) => (
  <div className="flex items-baseline justify-between gap-3 py-0.5" data-testid={testId}>
    <span className="min-w-0">{left}</span><span className="shrink-0 tabular-nums font-medium">{right}</span>
  </div>
);

const Group = ({ title, children }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-[#2D5F4F]">{title}</div>
    {children}
  </div>
);

const qtyText = (s, t) => [
  s.days ? `${s.days} ${t('dayUnit')}` : null, s.truck_days ? `${s.truck_days} ${t('truckDays')}` : null,
  s.unload_hours ? `${s.unload_hours} ${t('unloadHours')}` : null,
].filter(Boolean).join(' · ');

/** The work behind one day's pay: piece-rate operations, supervisor wage + incentive, contract day pay. */
export const PayDetail = ({ d, t, fx }) => {
  const p = d.pay;
  const c = p.contract;
  const f = p.fixed;
  return (
    <div className="mx-3 mb-2 space-y-2 rounded-lg border border-[#E5DFD0] bg-[#FBF8F1] px-3 py-2 text-sm" data-testid={`mypay-daydetail-${d.date}`}>
      {f && <Group title={t('fixedPay')}>
        {(f.days > 0 || !f.night_hours) && <Line testId={`mypay-daydetail-fixday-${d.date}`} right={!f.day_amount && p.hold ? t(HOLD[p.hold]) : rupees(f.day_amount)}
          left={<>{t('dayShift')} <span className="text-xs text-gray-500">{f.days} {t('dayUnit')} × {rupees(fx?.per_day)}</span></>} />}
        {f.night_hours > 0 && <Line testId={`mypay-daydetail-fixnight-${d.date}`} right={rupees(f.night_amount)}
          left={<>{t('nightShift')} <span className="text-xs text-gray-500">{f.night_hours} {t('hours')} × {rupees(fx?.night_rate)}</span></>} />}
      </Group>}
      {p.piece.length > 0 && <Group title={t('pieceRate')}>{p.piece.map((l, i) => (
        <Line key={i} testId={`mypay-daydetail-piece-${d.date}-${i}`} right={rupees(l.amount)}
          left={<>{l.operation} <span className="text-xs text-gray-500">{l.module} · {l.qty} {l.unit_type}</span></>} />
      ))}</Group>}
      {p.supervisor.length > 0 && <Group title={t('supervisor')}>{p.supervisor.map((r, i) => (
        <Line key={i} testId={`mypay-daydetail-sup-${d.date}-${i}`} right={rupees(r.amount)}
          left={<>{r.operation} <span className="text-xs text-gray-500">{t('wage')} {rupees(r.base_wage)} + {t('incentive')} {rupees(r.incentive)}</span></>} />
      ))}</Group>}
      {c && <Group title={t('contract')}>{c.sections.map((s, i) => (
        <Line key={i} testId={`mypay-daydetail-con-${d.date}-${i}`}
          right={s.amount == null ? t('rateNotSet') : !s.amount && p.hold ? t(HOLD[p.hold]) : rupees(s.amount)}
          left={<>{s.section} <span className="text-xs text-gray-500">{qtyText(s, t)}</span></>} />
      ))}</Group>}
      {p.hold && <p className="text-xs font-medium text-amber-800" data-testid={`mypay-daydetail-note-${d.date}`}>{t(NOTE[p.hold])}</p>}
      <div className="flex justify-between border-t border-[#E5DFD0] pt-1 font-semibold">
        <span>{t('dayTotal')}</span><span className="tabular-nums" data-testid={`mypay-daydetail-total-${d.date}`}>{rupees(p.amount)}</span>
      </div>
    </div>
  );
};
